import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { getProblemsDiscovery, getProblemsDiscoveryAggregated, getProblemsDiscoveryKPIs } from '../data/problemsApi';
import { useAuth } from '../context/AuthContext';

export const useAggregatedProblemsData = (viewMode = 'group', filters = {}, options = {}) => {
  const {
    pageSize = 10,
    initialPage = 1,
    sorter = null
  } = options;

  const { currentUser } = useAuth();
  // Mismo motivo que useProblemsTableData: las etiquetas vienen traducidas del backend vía
  // Accept-Language, así que el cambio de idioma exige refetch.
  const { i18n } = useTranslation();

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [totals, setTotals] = useState(null);
  const [paginationInfo, setPaginationInfo] = useState({
    currentPage: initialPage,
    totalPages: 0,
    totalCount: 0,
    pageSize: pageSize
  });
  // Al cambiar de vista estando en la página N salen dos peticiones (página N y, tras el reset,
  // la 1); sin esto ganaba la que llegara última y la tabla enseñaba la N con el "1" marcado.
  const latestRequestRef = useRef(0);

  // Convert filters to API parameters
  const buildApiParams = useCallback(() => {
    const params = {
      page: paginationInfo.currentPage,
      pageSize: paginationInfo.pageSize
    };

    // For aggregated view, add groupBy parameter
    if (viewMode !== 'unit') {
      params.groupBy = viewMode;
    }

    // Mapping from frontend filter keys to backend filter keys
    const filterKeyMapping = {
      'category': 'productCategory',
      'problemType': 'analysisType',
      'type': 'analysisType',
      'route': 'routes'
    };

    // Add filters
    Object.keys(filters).forEach(key => {
      if (filters[key] && filters[key].length > 0) {
        const backendKey = filterKeyMapping[key] || key;
        if (backendKey === 'routes') {
          params[backendKey] = filters[key].map(value => {
            const raw = value?.id ?? value;
            const parsed = parseInt(String(raw), 10);
            return Number.isNaN(parsed) ? raw : parsed;
          });
        } else {
          params[backendKey] = filters[key];
        }
      }
    });

    // Add sorting
    if (sorter && sorter.order) {
      const sortFieldFromUI = sorter.field || sorter.columnKey;
      if (sortFieldFromUI) {
        // Map frontend sort field to backend sort field
        const sortFieldMapping = {
          'product': 'category',
          'productName': 'product',
          'problemType': 'type',
          'groupingLabel': 'groupingLabel',
          'totalOpportunityCost': 'totalOpportunityCost',
          'machineCount': 'machineCount',
          'experimentCount': 'experimentCount'
        };

        const backendSortField = sortFieldMapping[sortFieldFromUI] || sortFieldFromUI;
        params.sortField = backendSortField;
        params.sortDirection = sorter.order === 'ascend' ? 'asc' : 'desc';
      }
    }

    return params;
  }, [filters, paginationInfo.currentPage, paginationInfo.pageSize, sorter, viewMode]);

  // Fetch data function
  const fetchData = useCallback(async () => {
    const clientId = currentUser?.customer_id;

    if (!clientId) {
      setData([]);
      setTotals(null);
      setLoading(false);
      return;
    }

    const requestId = ++latestRequestRef.current;
    setLoading(true);
    setError(null);

    try {
      const params = buildApiParams();
      let response;

      // Choose the appropriate API call based on viewMode
      if (viewMode === 'unit') {
        // Unit level view - use the original endpoint
        response = await getProblemsDiscovery(clientId, params);
        if (requestId !== latestRequestRef.current) return;

        // Format response to match expected structure
        setData(response.data);
        setTotals(response.kpis);
        setPaginationInfo(prev => ({
          ...prev,
          totalPages: response.pagination.totalPages,
          totalCount: response.pagination.totalCount
        }));
      } else {
        // Aggregated view - use the new aggregated endpoint
        response = await getProblemsDiscoveryAggregated(clientId, params);
        if (requestId !== latestRequestRef.current) return;

        // Format response to match expected structure
        setData(response.data);
        setTotals(response.totals);
        setPaginationInfo(prev => ({
          ...prev,
          totalPages: response.pagination.totalPages,
          totalCount: response.pagination.totalCount
        }));
      }
    } catch (err) {
      if (requestId !== latestRequestRef.current) return;
      console.error('Error fetching problems data:', err);
      setError(err.message || 'Failed to fetch problems data');
      setData([]);
      setTotals(null);
    } finally {
      if (requestId === latestRequestRef.current) {
        setLoading(false);
      }
    }
    // i18n.language no se lee en el cuerpo (viaja en el header Accept-Language que
    // createAuthHeader resuelve al vuelo), pero ES el disparador del refetch al cambiar idioma.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buildApiParams, filters, currentUser?.customer_id, viewMode, i18n.language]);

  // Reset pagination when viewMode changes
  useEffect(() => {
    setPaginationInfo(prev => ({
      ...prev,
      currentPage: 1
    }));
  }, [viewMode]);

  // Initial load and when filters or viewMode change
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Set page function
  const setPage = useCallback((newPage) => {
    setPaginationInfo(prev => ({
      ...prev,
      currentPage: newPage
    }));
  }, []);

  // Refetch function
  const refetch = useCallback(() => {
    fetchData();
  }, [fetchData]);

  // Refetch only KPIs (without table data) - used after experiment actions
  const refetchKPIs = useCallback(async () => {
    const clientId = currentUser?.customer_id;
    if (!clientId) return;

    try {
      const kpisResponse = await getProblemsDiscoveryKPIs(clientId);
      setTotals(kpisResponse);
    } catch (err) {
      console.error('Error refetching KPIs:', err);
    }
  }, [currentUser?.customer_id]);

  // Update a specific row's data locally (for optimistic updates)
  const updateRowData = useCallback((groupingKey, updates) => {
    setData(prevData => prevData.map(row => {
      if (row.groupingKey === groupingKey) {
        return { ...row, ...updates };
      }
      return row;
    }));
  }, []);

  // Remove a row from data (when all experiments are gone)
  const removeRow = useCallback((groupingKey) => {
    setData(prevData => prevData.filter(row => row.groupingKey !== groupingKey));
  }, []);

  return {
    data,
    loading,
    error,
    totals,
    paginationInfo,
    setPage,
    refetch,
    refetchKPIs,
    updateRowData,
    removeRow
  };
};